import React, { useMemo } from 'react'
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Alert,
  AlertTitle,
} from '@mui/material'
import { useTranslation, Trans } from 'react-i18next' // LAP-733 (GE022)
// Import removed - preview happens during final import

function DataCleaningStep({
  amountMode,
  signLogic,
  setSignLogic,
  signColumn,
  setSignColumn,
  fileData,
  columnMapping,
  accountInfo,
  setPreviewData,
  setError,
  setLoading,
  onNext,
  onBack,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)

  const signLogicOptions = useMemo(
    () => [
      {
        value: 'already_signed',
        label: t('mesComptes.import.step4.alreadySigned.label'),
        description: t('mesComptes.import.step4.alreadySigned.description'),
      },
      {
        value: 'credit_debit_column',
        label: t('mesComptes.import.step4.creditDebitColumn.label'),
        description: t('mesComptes.import.step4.creditDebitColumn.description'),
      },
      {
        value: 'keyword_detection',
        label: t('mesComptes.import.step4.keywordDetection.label'),
        description: t('mesComptes.import.step4.keywordDetection.description'),
      },
    ],
    [t]
  ) // LAP-733 (GE022)

  // En mode crédit/débit (mapping), le signe est déjà déterminé par la paire
  // de colonnes : la logique de signe de cette étape n'a plus de sens.
  const creditDebitMapped = amountMode === 'credit_debit'

  const handlePreview = async () => {
    if (creditDebitMapped) {
      setPreviewData({ sign_logic: 'already_signed', sign_column: '' })
      onNext()
      return
    }

    // Validate sign column if credit_debit_column logic is selected
    if (signLogic === 'credit_debit_column' && !signColumn) {
      setError(t('mesComptes.import.errors.selectSensColumn')) // LAP-733 (GE022)
      return
    }

    // Store cleaning configuration for the final import step
    setPreviewData({
      sign_logic: signLogic,
      sign_column: signColumn,
    })

    // Proceed to next step (Review & Confirm)
    // Note: Preview will happen during actual import since account doesn't exist yet
    onNext()
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('mesComptes.import.step4.title') /* LAP-733 (GE022) */}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('mesComptes.import.step4.description') /* LAP-733 (GE022) */}
      </Typography>

      {/* Date Auto-Detection Info */}
      <Alert severity="success" sx={{ mb: 3 }}>
        <AlertTitle>
          {t('mesComptes.import.step4.dateDetection.title') /* LAP-733 (GE022) */}
        </AlertTitle>
        {t('mesComptes.import.step4.dateDetection.intro') /* LAP-733 (GE022) */}
        <ul style={{ marginTop: 8, marginBottom: 0 }}>
          <li>{t('mesComptes.import.step4.dateDetection.bullet1') /* LAP-733 (GE022) */}</li>
          <li>{t('mesComptes.import.step4.dateDetection.bullet2') /* LAP-733 (GE022) */}</li>
          <li>{t('mesComptes.import.step4.dateDetection.bullet3') /* LAP-733 (GE022) */}</li>
          <li>{t('mesComptes.import.step4.dateDetection.bullet4') /* LAP-733 (GE022) */}</li>
          <li>{t('mesComptes.import.step4.dateDetection.bullet5') /* LAP-733 (GE022) */}</li>
        </ul>
        {t('mesComptes.import.step4.dateDetection.closing') /* LAP-733 (GE022) */}
      </Alert>

      {/* Sign Logic — masqué quand le mapping crédit/débit porte déjà le signe */}
      {creditDebitMapped ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>{t('mesComptes.import.step4.creditDebitMapped.title')}</AlertTitle>
          {t('mesComptes.import.step4.creditDebitMapped.body')}
        </Alert>
      ) : (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
          {t('mesComptes.import.step4.signLogic') /* LAP-733 (GE022) */}
        </FormLabel>
        <RadioGroup value={signLogic} onChange={(e) => setSignLogic(e.target.value)}>
          {signLogicOptions.map((option) => (
            <Box key={option.value} sx={{ mb: 2 }}>
              <FormControlLabel
                value={option.value}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {option.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                }
              />
              {option.value === 'credit_debit_column' && signLogic === 'credit_debit_column' && (
                <Box sx={{ ml: 4, mt: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      {t('mesComptes.import.step4.selectSensColumn') /* LAP-733 (GE022) */}
                    </InputLabel>
                    <Select
                      value={signColumn}
                      onChange={(e) => setSignColumn(e.target.value)}
                      label={t('mesComptes.import.step4.selectSensColumn') /* LAP-733 (GE022) */}
                    >
                      <MenuItem value="">
                        <em>{t('mesComptes.import.step3.none') /* LAP-733 (GE022) */}</em>
                      </MenuItem>
                      {fileData?.columns.map((col) => (
                        <MenuItem key={col} value={col}>
                          {col}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          ))}
        </RadioGroup>
      </Paper>
      )}

      {/* Amount Normalization Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>
          {t('mesComptes.import.step4.amountNormalization.title') /* LAP-733 (GE022) */}
        </AlertTitle>
        {t('mesComptes.import.step4.amountNormalization.intro') /* LAP-733 (GE022) */}
        <ul style={{ marginTop: 8, marginBottom: 0 }}>
          <li>{t('mesComptes.import.step4.amountNormalization.bullet1') /* LAP-733 (GE022) */}</li>
          <li>{t('mesComptes.import.step4.amountNormalization.bullet2') /* LAP-733 (GE022) */}</li>
          <li>{t('mesComptes.import.step4.amountNormalization.bullet3') /* LAP-733 (GE022) */}</li>
          <li>{t('mesComptes.import.step4.amountNormalization.bullet4') /* LAP-733 (GE022) */}</li>
        </ul>
      </Alert>

      {/* Navigation */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack}>
          {t('mesComptes.import.back') /* LAP-733 (GE022) */}
        </Button>
        <Button variant="contained" onClick={handlePreview}>
          {t('mesComptes.import.step4.nextButton') /* LAP-733 (GE022) */}
        </Button>
      </Box>
    </Box>
  )
}

export default DataCleaningStep
